// The next runs a pending App schedule card shows before the tap. They come from the server's scheduler
// (never computed here) and stay out of what the tap approves: the card binds the job and its cadence,
// and these runs move with the clock. When the first of them passes, the card asks again.
import { useEffect, useState } from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy from '@/i18n/ns/app-approvals';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { previewAppSchedule, type ScheduleChoice } from '@/workspace/apps/schedule-api';
import { scheduleNextWords } from './action-approval-copy';
import type { ActionView } from './action-confirmation';

// setTimeout's longest wait; a run further away is asked for again then.
const LONGEST_WAIT_MS = 2 ** 31 - 1;
// How long to wait before asking again when the server names a run that has passed by this browser's clock.
const CLOCK_SKEW_RETRY_MS = 15_000;

/** The cadence a pending schedule card will run at, or null: a card that pauses or leaves the schedule
 * paused shows no runs, and nor does an expired card or any decided one (its receipt names the runs the switch
 * returned). */
export function scheduleCardCadence(action: ActionView, now = Date.now()): ScheduleChoice | null {
  const { schedule, timezone, state } = action.args;
  return action.tool === 'set_app_schedule' &&
    action.status === 'pending' &&
    Date.parse(action.expiresAt) > now &&
    state === 'on' &&
    typeof schedule === 'string' &&
    typeof timezone === 'string'
    ? { cron: schedule, timezone }
    : null;
}

/** The pending card's next-runs sentence in the person's language, or null until the server has said. */
export function useScheduleCardRuns(action: ActionView): string | null {
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(appApprovalsCopy);
  const send = useSessionBoundFetch();
  const wanted = scheduleCardCadence(action);
  const cron = wanted?.cron;
  const timezone = wanted?.timezone;
  const [runs, setRuns] = useState<string[] | null>(null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    if (!cron || !timezone) {
      setRuns(null);
      return;
    }
    let live = true;
    void previewAppSchedule({ cron, timezone }, send).then((next) => {
      if (live) setRuns(next);
    });
    return () => {
      live = false;
    };
  }, [cron, timezone, send, asked]);
  // Each reply arms the next ask: a moment after its first run passes.
  useEffect(() => {
    const first = runs?.[0];
    if (!first) return;
    const until = Date.parse(first) - Date.now();
    // A server whose clock is behind still names a run this browser thinks has passed: ask again a little later.
    const wait = until > 0 ? Math.min(until + 1000, LONGEST_WAIT_MS) : CLOCK_SKEW_RETRY_MS;
    const timer = setTimeout(() => setAsked((count) => count + 1), wait);
    return () => clearTimeout(timer);
  }, [runs]);
  return runs && timezone ? scheduleNextWords(runs, timezone, copy, locale) : null;
}
